import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { TeamLiveDeviceEntry, TeamLiveStreamEntry } from '@/types';
import type { TeamExternalStream } from '@/utils/teamLiveExternalStreams';
import type { TeamLiveGridTile, TeamLiveTileExtras } from './TeamLiveSortableGrid';

export interface TeamLiveFocusViewerProps {
  tiles: TeamLiveGridTile[];
  /** Index into `tiles` of the focused stream. */
  index: number;
  onClose: () => void;
  /** +1 next / -1 previous in the saved tile order. */
  onNavigate: (delta: 1 | -1) => void;
  renderMemberTile: (
    entry: TeamLiveStreamEntry,
    drag: null,
    extra?: TeamLiveTileExtras,
  ) => ReactNode;
  renderDeviceTile: (entry: TeamLiveDeviceEntry, drag: null) => ReactNode;
  renderExternalTile: (
    stream: TeamExternalStream,
    drag: null,
    extra?: TeamLiveTileExtras,
  ) => ReactNode;
}

/** Swipe triggers after this much horizontal travel (px). */
const SWIPE_THRESHOLD_PX = 48;
/** Landscape strap auto-hide delay (matches LiveStreamPlayer controls). */
const HIDE_DELAY_MS = 3000;

/**
 * Fullscreen mobile focus viewer (2026-09-25 mobile tile layout spec):
 * immersive single stream over a black backdrop, swipe (or arrow buttons)
 * to move through the saved tile order. Portrait keeps the tile's docked
 * strap; landscape auto-hides it after 3s idle — tap restores.
 */
export function TeamLiveFocusViewer({
  tiles,
  index,
  onClose,
  onNavigate,
  renderMemberTile,
  renderDeviceTile,
  renderExternalTile,
}: TeamLiveFocusViewerProps) {
  const { t } = useTranslation('pages');
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const [strapVisible, setStrapVisible] = useState(true);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragStart = useRef<{ id: number; x: number; y: number } | null>(null);

  const revealStrap = useCallback(() => {
    setStrapVisible(true);
    if (hideTimer.current !== null) clearTimeout(hideTimer.current);
    hideTimer.current = null;
    if (isLandscape) {
      hideTimer.current = setTimeout(
        () => setStrapVisible(false),
        HIDE_DELAY_MS,
      );
    }
  }, [isLandscape]);

  useEffect(() => {
    revealStrap();
    return () => {
      if (hideTimer.current !== null) clearTimeout(hideTimer.current);
    };
  }, [revealStrap]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tile = tiles[index];
  if (!tile) return null;

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    revealStrap();
    if ((event.target as HTMLElement).closest('button')) return;
    dragStart.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    revealStrap();
    const start = dragStart.current;
    dragStart.current = null;
    if (!start || start.id !== event.pointerId) return;
    if ((event.target as HTMLElement).closest('button')) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) >= SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
      onNavigate(dx < 0 ? 1 : -1);
    }
  };

  // A claimed/aborted gesture (e.g. the browser taking a pan) must not leave a
  // stale start behind for the next pointerup to mis-measure.
  const handlePointerCancel = () => {
    dragStart.current = null;
  };

  const strapHidden = isLandscape && !strapVisible;

  return (
    <div
      data-testid="team-live-focus-viewer"
      className="fixed inset-0 z-50 bg-black [touch-action:pan-y]"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      <div className="h-full w-full">
        {tile.kind === 'member'
          ? renderMemberTile(tile.entry, null, { strapHidden })
          : tile.kind === 'device'
            ? renderDeviceTile(tile.entry, null)
            : // Extras (even empty) mark the viewer's copy: the page renders
              // it playing, without the grid twin's paused/expand gating.
              renderExternalTile(tile.stream, null, {})}
      </div>

      <button
        type="button"
        onClick={onClose}
        aria-label={t('teamLive.viewerClose')}
        className="absolute left-2 top-2 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm"
      >
        <X className="h-5 w-5" />
      </button>

      {tiles.length > 1 ? (
        <div className="absolute right-2 top-1/2 z-10 flex -translate-y-1/2 flex-col gap-2">
          <button
            type="button"
            onClick={() => onNavigate(-1)}
            aria-label={t('teamLive.viewerPrevious')}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => onNavigate(1)}
            aria-label={t('teamLive.viewerNext')}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
